import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { bookingApi } from '../../api/booking';
import { formatCurrencyFromMinor } from '../../utils/currency';
import { isOrgPaymentsNotReady, orgPaymentsNotReadyMessage } from '../../utils/orgPayments';
import SupplierPolicyAcknowledgement, { policyToSections } from '../../components/SupplierPolicyAcknowledgement';
import './BookingMagicLinkPaymentPage.css';
import { getStripePromise } from "../../utils/stripeKey";
import ServiceFeeInfo from "../../components/ServiceFeeInfo";


const formatDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    return d.toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const formatAmount = (cents, currency) => formatCurrencyFromMinor(cents, currency);

/**
 * PaymentForm — must be a child of <Elements> so Stripe hooks work
 */
const PaymentForm = ({ paymentData, onPaymentSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isPaying, setIsPaying] = useState(false);
    const [isPaid, setIsPaid] = useState(false);
    const [policyAck, setPolicyAck] = useState(false);
    const [elementsReady, setElementsReady] = useState(false);

    // The policy gate applies to a full-booking payment. A venue-change adjustment
    // (variant 'adjustment') has no policy payload, so it is never gated.
    const requireAck = !!paymentData?.privateLessonPolicy;

    const handlePay = async () => {
        if (!stripe || !elements || !elementsReady || !paymentData) return;
        if (requireAck && !policyAck) return;
        setIsPaying(true);
        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: 'if_required',
            });

            if (error) {
                toast.error(error.message || 'Payment failed. Please try again.');
                return;
            }

            if (paymentIntent?.status === 'succeeded') {
                setIsPaid(true);
                if (onPaymentSuccess) onPaymentSuccess();
            } else {
                toast.error('Payment not completed. Please try again.');
            }
        } catch {
            toast.error('Payment failed. Please try again.');
        } finally {
            setIsPaying(false);
        }
    };

    if (isPaid) {
        return (
            <div className="booking-payment-success-card">
                <h2>Payment Complete!</h2>
                <p>Your booking has been paid. See you at the lesson!</p>
                <p style={{ marginTop: '12px', fontSize: '0.85rem', color: 'var(--dl-color-medium)' }}>
                    You can <strong>close</strong> this window.
                </p>
            </div>
        );
    }

    return (
        <>
            {paymentData.variant !== 'adjustment' && paymentData.fees && (paymentData.fees.platformFee > 0 || paymentData.fees.processingFee > 0 || (paymentData.fees.venueFee || 0) > 0) && (
                <div className="booking-payment-fee-breakdown">
                    <div className="booking-payment-fee-row">
                        <span>Lesson fee</span>
                        <span>{formatAmount(
                            paymentData.fees.lessonFee !== undefined
                                ? paymentData.fees.lessonFee
                                : paymentData.fees.totalPrice - (paymentData.fees.venueFee || 0) - (paymentData.fees.platformFee || 0) - (paymentData.fees.processingFee || 0),
                            paymentData.currency,
                        )}</span>
                    </div>
                    {paymentData.fees.venueFee > 0 && (
                        <div className="booking-payment-fee-row">
                            <span>Venue fee</span>
                            <span>{formatAmount(paymentData.fees.venueFee, paymentData.currency)}</span>
                        </div>
                    )}
                    <div className="booking-payment-fee-row">
                        <ServiceFeeInfo />
                        <span>{formatAmount((paymentData.fees.platformFee || 0) + (paymentData.fees.processingFee || 0), paymentData.currency)}</span>
                    </div>
                </div>
            )}

            <div className="booking-payment-total">
                <span>{paymentData.variant === 'adjustment' ? 'Amount owed' : 'Total Due'}</span>
                <span className="booking-payment-amount">
                    {formatAmount(paymentData.amount, paymentData.currency)}
                </span>
            </div>

            {requireAck && (
                <SupplierPolicyAcknowledgement
                    sections={policyToSections(paymentData.privateLessonPolicy)}
                    supplierName={paymentData.instructorName}
                    surfaceLabel="lesson"
                    acknowledged={policyAck}
                    onAcknowledgedChange={setPolicyAck}
                />
            )}

            <div className="booking-payment-card-element">
                <PaymentElement onReady={() => setElementsReady(true)} onLoadError={(e) => toast.error(e?.error?.message || 'Could not load the payment form. Please try again.')} options={{ layout: 'tabs', paymentMethodOrder: ['apple_pay', 'google_pay', 'card'] }} />
            </div>

            <button
                className="booking-payment-btn"
                onClick={handlePay}
                disabled={isPaying || !stripe || !elements || !elementsReady || (requireAck && !policyAck)}>
                {isPaying ? 'Processing...' : `Pay ${formatAmount(paymentData.amount, paymentData.currency)}`}
            </button>

            <p className="booking-payment-secure-note">
                Secured by Stripe. Your card details are never stored.
            </p>
        </>
    );
};

/**
 * BookingMagicLinkPaymentPage — Public payment page for private-lesson bookings.
 * Route: /booking-payment/:qrToken
 * No auth required — accessed via email magic link.
 */
const BookingMagicLinkPaymentPage = () => {
    const { qrToken } = useParams();

    const [paymentData, setPaymentData] = useState(null);
    const [clientSecret, setClientSecret] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [orgNotReady, setOrgNotReady] = useState(false);

    // Apply org branding via --school-* CSS vars
    useEffect(() => {
        const branding = paymentData?.branding;
        if (!branding) return;
        const root = document.getElementById('booking-payment-root');
        if (!root) return;
        if (branding.buttonColor) root.style.setProperty('--school-btn-color', branding.buttonColor);
        if (branding.buttonTextColor) root.style.setProperty('--school-btn-text', branding.buttonTextColor);
        if (branding.headerColor) root.style.setProperty('--school-header-color', branding.headerColor);
        if (branding.headerTextColor) root.style.setProperty('--school-header-text', branding.headerTextColor);
        if (branding.backgroundColor) root.style.setProperty('--school-bg-color', branding.backgroundColor);
    }, [paymentData]);

    useEffect(() => {
        const fetchPaymentData = async () => {
            try {
                const response = await bookingApi.resolveBookingPaymentToken(qrToken);
                if (response.ok) {
                    setPaymentData(response.data);
                    // The resolver tags the token: 'adjustment' (venue-change top-up — delta only)
                    // vs 'full-booking'. Each routes to its own PaymentIntent endpoint.
                    const intentResponse = response.data.variant === 'adjustment'
                        ? await bookingApi.createBookingAdjustmentIntent(response.data.bookingId)
                        : await bookingApi.createBookingPaymentIntent(response.data.bookingId);
                    if (intentResponse.ok) {
                        setClientSecret(intentResponse.data.clientSecret);
                    } else if (isOrgPaymentsNotReady(intentResponse)) {
                        setOrgNotReady(true);
                    } else {
                        setError(intentResponse.data?.message || 'Failed to initialise payment');
                    }
                } else {
                    setError(response.data?.message || 'Payment link not found');
                }
            } catch {
                setError('Failed to load payment details');
            } finally {
                setIsLoading(false);
            }
        };
        fetchPaymentData();
    }, [qrToken]);

    if (isLoading) {
        return (
            <div className="booking-payment-page" id="booking-payment-root">
                <div className="booking-payment-loading">Loading payment details...</div>
            </div>
        );
    }

    if (orgNotReady) {
        return (
            <div className="booking-payment-page" id="booking-payment-root">
                <div className="booking-payment-error-card">
                    <h2>Online Payments Not Available</h2>
                    <p>{orgPaymentsNotReadyMessage(paymentData?.orgName, 'your instructor')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="booking-payment-page" id="booking-payment-root">
                <div className="booking-payment-error-card">
                    <h2>Payment Link Invalid</h2>
                    <p>{error}</p>
                    <p>This link may have expired or already been used. Please ask your instructor for a new link.</p>
                </div>
            </div>
        );
    }

    // Resolve Stripe appearance colors via getComputedStyle — Stripe iframe cannot read CSS vars directly
    const primaryColor = paymentData?.branding?.buttonColor ||
        getComputedStyle(document.documentElement).getPropertyValue('--dl-color-purple-default').trim() ||
        '#451f78';

    return (
        <div className="booking-payment-page" id="booking-payment-root">
            <div className="booking-payment-header">
                <h1 className="booking-payment-org-name">{paymentData.orgName}</h1>
                <p className="booking-payment-subtitle">
                    {paymentData.variant === 'adjustment' ? 'Venue change adjustment' : 'Private Lesson Payment'}
                </p>
                {paymentData.studentFirstName && paymentData.studentFirstName !== 'there' && (
                    <p className="booking-payment-student-name">For {paymentData.studentFirstName}</p>
                )}
            </div>

            <div className="booking-payment-card">
                <div className="booking-payment-booking-info">
                    {paymentData.bookingDate && (
                        <div className="booking-payment-info-row">
                            <span className="booking-payment-info-label">Date</span>
                            <span className="booking-payment-info-value">{formatDate(paymentData.bookingDate)}</span>
                        </div>
                    )}
                    {paymentData.timeSlot && (
                        <div className="booking-payment-info-row">
                            <span className="booking-payment-info-label">Time</span>
                            <span className="booking-payment-info-value">{paymentData.timeSlot.startTime} – {paymentData.timeSlot.endTime}</span>
                        </div>
                    )}
                    {paymentData.instructorName && (
                        <div className="booking-payment-info-row">
                            <span className="booking-payment-info-label">Instructor</span>
                            <span className="booking-payment-info-value">{paymentData.instructorName}</span>
                        </div>
                    )}
                </div>

                {clientSecret ? (
                    <Elements stripe={getStripePromise()} options={{
                        clientSecret,
                        appearance: {
                            theme: 'stripe',
                            variables: {
                                colorPrimary: primaryColor,
                                colorBackground: '#ffffff',
                                colorText: '#1a1a1a',
                                borderRadius: '8px',
                                fontFamily: 'system-ui, sans-serif',
                            },
                        },
                    }}>
                        <PaymentForm paymentData={paymentData} />
                    </Elements>
                ) : (
                    <div className="booking-payment-loading" style={{ padding: '20px', textAlign: 'center' }}>
                        Preparing payment...
                    </div>
                )}

                {/* Bank-transfer details intentionally NOT rendered on the public payment
                    landing page — those live only in the booking-payment email. Surfacing
                    them here would give the student an alternative path that bypasses
                    Stripe, increasing the risk of non-payment. The Pay Now flow keeps the
                    payer on-rails through Stripe. The `paymentData.bankTransfer` payload
                    is still resolved by the backend (used by the email template). */}

                {/* No "Cancel" control here: this is a public magic-link page opened from
                    an email/QR, so window.history.back() had nowhere to go (issue #5). The
                    student either pays or closes the tab — the "Secured by Stripe" note covers it. */}
            </div>
        </div>
    );
};

export default BookingMagicLinkPaymentPage;
